"use client"

import React, { useMemo } from "react"
import { Modal, Tag } from "antd"
import { collectGenerationInputImages, formatFloorGenerationImageKey, formatRoomPlannerOutputLabel, FLOOR_DESIGN_LABEL, getFeatureDashboardIcon, getFeatureLabelIfKnown, getFeatureModalOutputImageStyle, getRoomPlannerModalGridLayout, matchesFloorDesignTitle, resolveTimelineFeatureKey } from "../../../utils/organizationApiHelpers"

import GenerationPresetsFooter from "./GenerationPresetsFooter"
import FurnitureDesignPresetsFooter from "./FurnitureDesignPresetsFooter"

/**
 * Furniture Design returns two kinds of rows in one ai_feature_detail list:
 * the piece render (step "furniture_piece") and the same piece placed in a
 * generated room (step "furniture_room"). Each row gets its own step heading.
 */
const FURNITURE_DESIGN_STEP_LABELS = {
  furniture_piece: { en: "Furniture Piece", ar: "قطعة الأثاث" },
  furniture_room: { en: "Product in Environment", ar: "المنتج في المساحة" },
}

const resolveFurnitureDesignStepLabel = (gen, lang) => {
  const step = String(gen?.step ?? "").trim().toLowerCase()
  const label = FURNITURE_DESIGN_STEP_LABELS[step]
  if (!label) return ""
  return lang === "ar" ? label.ar : label.en
}

const labeledModalFields = (labelEn, labelAr = labelEn) => ({
  headingEn: labelEn,
  headingAr: labelAr,
  italicEn: labelEn,
  italicAr: labelAr,
  imageClickLabelEn: labelEn,
  imageClickLabelAr: labelAr,
})

const resolveFeatureConfig = (title, featureKey = "") => {
  const key = String(featureKey ?? "").trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_")

  if (key === "furniture_replace") {
    return {
      icon: getFeatureDashboardIcon("furniture_replace", "/dashboard/replace-furniture-blue.svg"),
      headingEn: "Furniture Replace",
      headingAr: "استبدال الأثاث",
      italicEn: "Furniture Replace",
      italicAr: "استبدال الأثاث",
      imageClickLabelEn: "Furniture Replace",
      imageClickLabelAr: "استبدال الأثاث",
    }
  }
  if (key === "rearrange_furniture") {
    return {
      icon: getFeatureDashboardIcon("rearrange_furniture", "/dashboard/rearrange-furniture-blue.svg"),
      ...labeledModalFields("Rearrange Furniture", "إعادة ترتيب الأثاث"),
    }
  }
  if (key === "reference_style") {
    return {
      icon: getFeatureDashboardIcon("reference_style"),
      headingEn: "Reference Style",
      headingAr: "نمط المرجع",
      italicEn: "Reference Style",
      italicAr: "نمط المرجع",
      imageClickLabelEn: "Reference Style",
      imageClickLabelAr: "نمط المرجع",
    }
  }
  if (key === "design_with_product") {
    return {
      icon: getFeatureDashboardIcon("design_with_product"),
      headingEn: "Design With Product",
      headingAr: "التصميم بالمنتجات",
      italicEn: "Design With Product",
      italicAr: "التصميم بالمنتجات",
      imageClickLabelEn: "Design With Product",
      imageClickLabelAr: "التصميم بالمنتجات",
    }
  }
  if (key === "product_visualizer") {
    return {
      icon: getFeatureDashboardIcon("product_visualizer"),
      headingEn: "Replicate Design",
      headingAr: "استنساخ التصميم",
      italicEn: "Replicate Design",
      italicAr: "استنساخ التصميم",
      imageClickLabelEn: "Replicate Design",
      imageClickLabelAr: "استنساخ التصميم",
    }
  }
  if (key === "furniture_design") {
    return {
      icon: getFeatureDashboardIcon("furniture_design"),
      ...labeledModalFields("Furniture Design", "تصميم الأثاث"),
    }
  }
  if (key === "wall_design") {
    return {
      icon: getFeatureDashboardIcon("wall_design"),
      headingEn: "Wall Design",
      headingAr: "تصميم الجدار",
      italicEn: "Wall Design",
      italicAr: "تصميم الجدار",
      imageClickLabelEn: "Wall Design",
      imageClickLabelAr: "تصميم الجدار",
    }
  }
  if (key === "floor_design") {
    return {
      icon: getFeatureDashboardIcon("floor_design"),
      ...labeledModalFields(FLOOR_DESIGN_LABEL),
    }
  }
  if (key === "enhance_image") {
    return {
      icon: getFeatureDashboardIcon("enhance_image", "/dashboard/image-enhannce.svg"),
      headingEn: "Enhance Image",
      headingAr: "تحسين الصورة",
      italicEn: "Enhance Image",
      italicAr: "تحسين الصورة",
      imageClickLabelEn: "Enhance Image",
      imageClickLabelAr: "تحسين الصورة",
    }
  }
  if (key === "expert_design") {
    return {
      icon: getFeatureDashboardIcon("expert_design", "/dashboard/event-booth-timeline.svg"),
      headingEn: "Event Booth",
      headingAr: "بوث الفعاليات",
      italicEn: "Event Booth",
      italicAr: "بوث الفعاليات",
      imageClickLabelEn: "Event Booth",
      imageClickLabelAr: "بوث الفعاليات",
    }
  }

  const t = title || ""
  if (t.includes("Furniture Replace")) {
    return {
      icon: "/dashboard/replace-furniture-blue.svg",
      headingEn: "Furniture Replace",
      headingAr: "استبدال الأثاث",
      italicEn: "Furniture Replace",
      italicAr: "استبدال الأثاث",
      imageClickLabelEn: "Furniture Replace",
      imageClickLabelAr: "استبدال الأثاث",
    }
  }
  if (t.includes("Reference Style") || t.includes("نمط المرجع")) {
    return {
      icon: "/dashboard/reference-style-icon.svg",
      headingEn: "Reference Style",
      headingAr: "نمط المرجع",
      italicEn: "Reference Style",
      italicAr: "نمط المرجع",
      imageClickLabelEn: "Reference Style",
      imageClickLabelAr: "نمط المرجع",
    }
  }
  if (
    t.includes("Furniture Design") ||
    t.includes("Added Furniture to Environment") ||
    t.includes("تصميم الأثاث") ||
    t.includes("تصميم الاثاث") ||
    t.includes("إضافة الأثاث إلى المساحة") ||
    t.includes("إضافة الاثاث إلى المساحة")
  ) {
    return {
      icon: getFeatureDashboardIcon("furniture_design"),
      ...labeledModalFields("Furniture Design", "تصميم الأثاث"),
    }
  }
  if (t.includes("Wall Design") || t.includes("تصميم الجدار")) {
    return {
      icon: "/dashboard/wall-design-icon.svg",
      headingEn: "Wall Design",
      headingAr: "تصميم الجدار",
      italicEn: "Wall Design",
      italicAr: "تصميم الجدار",
      imageClickLabelEn: "Wall Design",
      imageClickLabelAr: "تصميم الجدار",
    }
  }
  if (matchesFloorDesignTitle(t)) {
    return {
      icon: getFeatureDashboardIcon("floor_design"),
      ...labeledModalFields(FLOOR_DESIGN_LABEL),
    }
  }
  if (
    t.includes("Room Planner") ||
    t.includes("Floor Generation") ||
    t.includes("مخطط الغرفة") ||
    t.includes("إنشاء الأرضية")
  ) {
    return {
      icon: "/dashboard/floor-generation-icon.svg",
      headingEn: "Room Planner",
      headingAr: "مخطط الغرفة",
      italicEn: "Room planner render",
      italicAr: "عرض مخطط الغرفة",
      imageClickLabelEn: "Room Planner",
      imageClickLabelAr: "مخطط الغرفة",
    }
  }
  if (t.includes("Enhance Image")) {
    return {
      icon: "/dashboard/image-enhannce.svg",
      headingEn: "Enhance Image",
      headingAr: "تحسين الصورة",
      italicEn: "Enhance Image",
      italicAr: "تحسين الصورة",
      imageClickLabelEn: "Enhance Image",
      imageClickLabelAr: "تحسين الصورة",
    }
  }
  if (
    t.toLowerCase().includes("expert design") ||
    t.includes("expert_design") ||
    t.includes("تصميم الخبير") ||
    t.includes("بوث الفعاليات")
  ) {
    return {
      icon: "/dashboard/event-booth-timeline.svg",
      headingEn: "Event Booth",
      headingAr: "بوث الفعاليات",
      italicEn: "Event Booth",
      italicAr: "بوث الفعاليات",
      imageClickLabelEn: "Event Booth",
      imageClickLabelAr: "بوث الفعاليات",
    }
  }
  return {
    icon: "/dashboard/image-enhannce.svg",
    headingEn: "Enhance Image",
    headingAr: "تحسين الصورة",
    italicEn: "Enhance Image",
    italicAr: "تحسين الصورة",
    imageClickLabelEn: "Enhance Image",
    imageClickLabelAr: "تحسين الصورة",
  }
}

/** Ordered image URLs for one timeline generation (Event Booth: output + secondary + third). */
const collectGenerationOutputUrls = (gen) => {
  if (!gen || typeof gen !== "object") return []
  const keys = ["output", "output_secondary", "output_third"]
  const urls = []
  for (const k of keys) {
    const v = gen[k]
    if (typeof v === "string" && v.trim()) urls.push(v.trim())
  }
  return urls
}

/** Room planner / floor generation images from ai_feature_detail list items. */
const collectGenerationGalleryImages = (gen) => {
  if (!gen || typeof gen !== "object" || !Array.isArray(gen.images)) return []
  const seen = new Set()
  const out = []
  for (const [index, item] of gen.images.entries()) {
    const url =
      typeof item === "string"
        ? item.trim()
        : typeof item?.url === "string"
        ? item.url.trim()
        : ""
    if (!url || seen.has(url)) continue
    seen.add(url)
    out.push({
      key:
        (item && typeof item === "object" && item.key) ||
        `image_${index}`,
      url,
    })
  }
  const output =
    typeof gen.output === "string" && gen.output.trim()
      ? gen.output.trim()
      : ""
  if (output && !seen.has(output)) {
    out.push({ key: "compose_primary", url: output })
  }
  return out
}

const getGenerationDisplayImages = (gen) => {
  const gallery = collectGenerationGalleryImages(gen)
  if (gallery.length > 0) return gallery
  return collectGenerationOutputUrls(gen).map((url, index) => ({
    key: index === 0 ? "output" : `output_${index + 1}`,
    url,
  }))
}

const formatGenerationStamp = (gen, fallbackDate = null) => {
  const parts = [gen?.date, gen?.time].filter(
    (value) => value != null && String(value).trim() !== ""
  )
  if (parts.length) return parts.join(" ")
  if (fallbackDate) return String(fallbackDate)
  return "—"
}

const hasGenerationDetailPayload = (detail) => {
  if (!detail || typeof detail !== "object") return false
  if (Array.isArray(detail.list) && detail.list.length > 0) return true
  return Boolean(
    detail.output ||
      detail.input ||
      detail.image_url ||
      detail.uploaded_img_url ||
      detail.img_url ||
      detail.ref_url ||
      detail.reference_image ||
      detail.presets
  )
}

const InputOutputFeatureModal = ({ open, onClose, data, onImageClick }) => {
  const lang = localStorage.getItem("language") || "ar"

  const detail = data?.detail || {}
  const generations =
    Array.isArray(detail.list) && detail.list.length > 0
      ? detail.list
      : hasGenerationDetailPayload(detail)
      ? [detail]
      : []
  const hasData = generations.length > 0

  const featureKey =
    data?.featureKey ||
    resolveTimelineFeatureKey({
      title: data?.title,
      title_arabic: data?.title_arabic,
      featureKey: data?.featureKey,
    }) ||
    ""
  const showPresetsFooter =
    featureKey === "reference_style" ||
    featureKey === "wall_design" ||
    featureKey === "floor_design"

  const config = useMemo(
    () =>
      resolveFeatureConfig(
        `${data?.title || ""} ${data?.title_arabic || ""}`.trim(),
        featureKey
      ),
    [data?.title, data?.title_arabic, featureKey]
  )

  const heading =
    (featureKey && getFeatureLabelIfKnown(featureKey, lang)) ||
    (lang === "ar" ? config.headingAr : config.headingEn)
  const italicLine = lang === "ar" ? config.italicAr : config.italicEn
  const imageClickLabel =
    lang === "ar" ? config.imageClickLabelAr : config.imageClickLabelEn

  const headerStamp = hasData
    ? formatGenerationStamp(generations[0], data?.date)
    : "—"

  return (
    <Modal
      title={
        <div>
          <div className="d-flex">
            <div
              className={
                lang === "ar" ? "r-r-modal-icon-arabic" : "r-r-modal-icon"
              }
            >
              <img src={config.icon} alt="" style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <div className="modal-heading" style={{ marginBottom: 0 }}>
                {heading}
              </div>
              <p className="modal-timestamp">{headerStamp}</p>
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "75vh",
        padding: "0 12px",
      }}
    >
      <div style={{ flex: "0 0 auto", paddingTop: 16 }}>
        <p
          style={{
            fontStyle: "italic",
            fontWeight: 600,
            marginBottom: 12,
            color: "#404040",
          }}
        >
          {italicLine}
        </p>
      </div>

      <div
        className="scrollable-cards"
        style={{
          flex: "1 1 auto",
          overflowY: "auto",
          marginTop: 4,
          paddingBottom: 16,
        }}
      >
        {hasData ? (
          generations.map((gen, index) => {
            const displayImages = getGenerationDisplayImages(gen)
            const inputImages = collectGenerationInputImages(
              gen,
              featureKey,
              `${data?.title || ""} ${data?.title_arabic || ""}`.trim()
            )
            const isRoomPlannerGallery =
              Array.isArray(gen.images) && gen.images.length > 0
            const stepLabel =
              featureKey === "furniture_design"
                ? resolveFurnitureDesignStepLabel(gen, lang)
                : ""

            return (
            <div className="quick-edits" key={index}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 22,
                      height: 22,
                      padding: "0 6px",
                      background: "#308aa3",
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 500,
                      borderRadius: 999,
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="modal-div-date">
                    &nbsp; {formatGenerationStamp(gen, data?.date)}
                  </span>
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {stepLabel ? <Tag color="blue">{stepLabel}</Tag> : null}
                  {gen.status === "success" || gen.status !== "failed" ? (
                    <Tag color="green">
                      {lang === "ar" ? "ناجح" : "Success"}
                    </Tag>
                  ) : (
                    <Tag color="red">{lang === "ar" ? "فشل" : "Failed"}</Tag>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                {inputImages.length > 0 ? (
                  <div
                    style={{
                      flex: 1,
                      border: "1px solid #E9EAEB",
                      borderRadius: "10px",
                      padding: "12px",
                      background: "#fafafa",
                      minWidth: "280px",
                    }}
                  >
                    <div className="modal-input-label">
                      {lang === "ar" ? "المدخل" : "Input"}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {inputImages.map((image, inputIdx) => {
                        const imageLabel =
                          lang === "ar" ? image.labelAr : image.labelEn
                        return (
                          <div key={`${image.key}-${inputIdx}`}>
                            {inputImages.length > 1 && (
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: "#667085",
                                  marginBottom: 8,
                                }}
                              >
                                {imageLabel}
                              </div>
                            )}
                            <img
                              src={image.url}
                              alt={imageLabel}
                              onClick={() => {
                                if (onImageClick) {
                                  onImageClick(image.url, imageLabel)
                                }
                              }}
                              style={{
                                ...getFeatureModalOutputImageStyle(),
                                cursor: onImageClick ? "pointer" : "default",
                                transition: "opacity 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                if (onImageClick) {
                                  e.currentTarget.style.opacity = "0.85"
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (onImageClick) {
                                  e.currentTarget.style.opacity = "1"
                                }
                              }}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : null}

                <div
                  style={{
                    flex: 1,
                    flexBasis: inputImages.length > 0 ? "280px" : "100%",
                    minWidth: inputImages.length > 0 ? "280px" : 0,
                    border: "1px solid #E9EAEB",
                    borderRadius: "10px",
                    padding: "12px",
                    background:
                      gen.status === "failed"
                        ? "#fff5f5"
                        : "rgba(250,250,250,0.9)",
                  }}
                >
                  {gen.status === "failed" ? (
                    <div
                      style={{
                        color: "#d93025",
                        border: "1px solid #f5c2c2",
                        borderRadius: "8px",
                        padding: "10px",
                        fontWeight: 500,
                      }}
                    >
                      {lang === "ar" ? "السبب:" : "Reason:"}
                      <br />
                      <span style={{ color: "red" }}>
                        {gen.reason ||
                          (lang === "ar"
                            ? "لم يتم توفير سبب"
                            : "No reason provided")}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#404040",
                          paddingBottom: "10px",
                        }}
                      >
                        {isRoomPlannerGallery
                          ? lang === "ar"
                            ? "صور مخطط الغرفة"
                            : "Room planner images"
                          : lang === "ar"
                          ? "الناتج"
                          : "Output"}
                      </div>
                      {displayImages.length > 0 ? (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              displayImages.length >= 3
                                ? "repeat(3, minmax(0, 1fr))"
                                : `repeat(${displayImages.length}, minmax(0, 1fr))`,
                            gap: 12,
                          }}
                        >
                          {displayImages.map((image, outIdx) => {
                            const keyedLabel = isRoomPlannerGallery
                              ? formatFloorGenerationImageKey(image.key, lang)
                              : null
                            const imageLabel = isRoomPlannerGallery
                              ? keyedLabel ||
                                formatRoomPlannerOutputLabel(outIdx, lang)
                              : displayImages.length > 1
                              ? lang === "ar"
                                ? `زاوية ${outIdx + 1}`
                                : `Angle ${outIdx + 1}`
                              : imageClickLabel
                            const layout = isRoomPlannerGallery
                              ? getRoomPlannerModalGridLayout(image.key)
                              : null

                            return (
                            <div
                              key={`${image.key}-${outIdx}`}
                              style={
                                layout?.wide
                                  ? { gridColumn: "1 / -1" }
                                  : undefined
                              }
                            >
                              {(isRoomPlannerGallery ||
                                displayImages.length > 1) && (
                                <div
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: "#667085",
                                    marginBottom: 8,
                                  }}
                                >
                                  {imageLabel}
                                </div>
                              )}
                              <img
                                src={image.url}
                                alt={imageLabel}
                                onClick={() => {
                                  if (onImageClick) {
                                    onImageClick(image.url, imageLabel)
                                  }
                                }}
                                style={{
                                  ...(layout
                                    ? layout.imageStyle
                                    : getFeatureModalOutputImageStyle()),
                                  cursor: onImageClick ? "pointer" : "default",
                                  transition: "opacity 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  if (onImageClick) {
                                    e.currentTarget.style.opacity = "0.85"
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (onImageClick) {
                                    e.currentTarget.style.opacity = "1"
                                  }
                                }}
                              />
                            </div>
                            )
                          })}
                        </div>
                      ) : (
                        <p
                          style={{
                            margin: 0,
                            fontSize: 13,
                            color: "#888",
                          }}
                        >
                          {lang === "ar"
                            ? "لا توجد صورة مخرجات"
                            : "No output image"}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {featureKey === "furniture_design" ? (
                <FurnitureDesignPresetsFooter gen={gen} lang={lang} />
              ) : showPresetsFooter ? (
                <GenerationPresetsFooter
                  gen={gen}
                  lang={lang}
                  includeCreativity
                />
              ) : null}
            </div>
            )
          })
        ) : (
          <p
            style={{
              color: "#888",
              textAlign: "center",
              marginTop: 20,
            }}
          >
            {lang === "ar"
              ? "لم يتم العثور على بيانات توليد"
              : "No generation data found"}
          </p>
        )}
      </div>
    </Modal>
  )
}

export default InputOutputFeatureModal
